<template>
  <div :key="newFormKey">
    <v-alert
      v-if="draftSnackbar && !isEditing"
      type="info"
      outlined
      class="mb-3"
      style="border-radius: 8px"
    >
      <div class="d-flex align-center justify-space-between flex-wrap gap-2">
        <span>
          <v-icon left small>mdi-content-save</v-icon>
          {{ $t('Você tem um rascunho de relatório não finalizado. Deseja continuar de onde parou?') }}
        </span>
        <div>
          <v-btn text color="info" small @click="loadDraft">{{ $t('Continuar rascunho') }}</v-btn>
          <v-btn text color="error" small @click="clearDraft">{{ $t('Descartar') }}</v-btn>
        </div>
      </div>
    </v-alert>

    <v-window v-model="step" class="reports-step-window">
      <div
        class="d-flex align-center justify-space-between"
        style="width: 100%"
      >
        <strong class="primary--text ml-2">{{
          $t('Gerar novo relatório')
        }}</strong>
        <v-btn
          class="primary-button mr-0"
          small
          v-if="isEditing"
          @click="reset"
        >
          {{ $t('Gerar novo relatório') }}
        </v-btn>
      </div>

      <v-divider class="ma-3"></v-divider>

      <v-window-item>
        <SelectInst :generalInfo.sync="generalInfo" @next="setGeneralInfo" />
      </v-window-item>
      <v-window-item>
        <SelectCharts
          :isEditing="isEditing"
          :selectionProp.sync="selection"
          @next="setSelection"
          @back="step--"
        />
      </v-window-item>
      <v-window-item>
        <ConfigureCharts
          :isEditing="isEditing"
          :chartInfosProp="chartInfos"
          :selection.sync="selection"
          :unidades="generalInfo.unidadeIds"
          @refresh="refreshData"
          @saveDraft="onSaveDraft"
          @next="setChartInfos"
          @back=";(key = !key), step--"
        />
      </v-window-item>
      <v-window-item>
        <Result
          :key="key"
          :selection.sync="selection"
          :link="generalInfo.link"
          :institutionName="generalInfo.institutionName"
          :unidades="generalInfo.unidadeIds"
          :reviser="generalInfo.reviser"
          :toCareUser="generalInfo.toCareUser"
          :chartInfos.sync="chartInfos"
          :lastEvents="generalInfo.lastEvents"
          :nextEvents="generalInfo.nextEvents"
          :isEditing="isEditing"
          :reportId="reportId"
          @back="step--"
          @confirm="getGenerateds(), clearDraft(), (newFormKey = !newFormKey), (step = 0)"
        />
      </v-window-item>
    </v-window>

    <div class="mt-4" v-if="step == 0">
      <v-divider class="my-3"></v-divider>

      <strong class="primary--text">
        {{ $t('Relatórios gerados anteriormente') }}
      </strong>
      <v-divider class="my-3"></v-divider>

      <!-- Loading state for the report listing -->
      <div
        v-if="loadingReports"
        class="d-flex flex-column align-center justify-center pa-6"
        style="
          min-height: 160px;
          border: 1px dashed #e0e5e9;
          border-radius: 12px;
          background: linear-gradient(180deg, #fafbfc 0%, #f4f6f8 100%);
        "
      >
        <v-progress-circular
          indeterminate
          size="48"
          width="4"
          color="primary"
        />
        <span
          class="mt-3"
          style="font-size: 13px; color: #5a6678; font-weight: 500"
        >
          {{ $t('Carregando relatórios...') }}
        </span>
      </div>

      <!-- Empty state -->
      <v-alert
        v-else-if="!reports || reports.length === 0"
        type="info"
        outlined
        rounded="lg"
        class="my-3"
      >
        {{
          $t('Nenhum relatório gerado ainda. Use o botão acima para criar um.')
        }}
      </v-alert>

      <v-data-table
        v-else
        :headers="headers"
        :items="reports"
        :loading="loadingReports"
        :loading-text="$t('Carregando...')"
        class="main-table"
        hide-default-footer
        hide-default-header
        :no-data-text="$t('Nenhum dado encontrado')"
      >
        <template #header="{ props, on }">
          <thead>
            <tr>
              <th
                class="text-center"
                v-for="header in props.headers"
                :key="header.value"
              >
                <v-simple-checkbox
                  v-if="header.value === 'data-table-select'"
                  v-model="props.everyItem"
                  :indeterminate="props.someItems && !props.everyItem"
                  color="purple"
                  @input="on['toggle-select-all']"
                ></v-simple-checkbox>

                <div>
                  <v-hover v-slot="{ hover }">
                    <div>
                      <span>{{ $t(header.text) }}</span>
                      <v-icon
                        v-if="
                          hover &&
                          header.value != 'data-table-select' &&
                          header.value != 'actions'
                        "
                        color="white"
                        class="mx-2"
                        @click="on.sort(header.value)"
                      >
                        {{
                          props.options.sortBy.includes(header.value)
                            ? `mdi-chevron-down`
                            : `mdi-chevron-up`
                        }}
                      </v-icon>
                    </div>
                  </v-hover>
                </div>
              </th>
            </tr>
          </thead>
        </template>

        <template #[`item.createdAt`]="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>

        <template #[`item.link`]="{ item }">
          {{ item.link ? item.link : $t('Sem registro') }}
        </template>

        <template #[`item.actions`]="{ item }">
          <v-btn
            text
            color="primary"
            :to="`/generatedReport/${item.link}`"
            target="_blank"
          >
            {{ $t('Detalhar') }}
          </v-btn>

          <v-btn text color="primary" @click="getReport(item.id)">
            {{ $t('Editar') }}
          </v-btn>

          <v-btn text color="primary" @click="copy(item.link)">
            {{ $t('Copiar URL de acesso') }}
          </v-btn>

          <v-btn
            text
            color="primary"
            @click=";(reportId = item.id), refreshData()"
          >
            {{ $t('Atualizar dados') }}
          </v-btn>

          <v-btn
            text
            color="error"
            @click="openDeleteConfirm(item)"
            v-if="institution.userType <= 2 || institution.userType == 8"
          >
            <v-icon>mdi-trash-can-outline</v-icon>
            {{ $t('Remover') }}
          </v-btn>
        </template>
      </v-data-table>

      <v-dialog v-model="showDeleteConfirm" max-width="300">
        <div class="text-center">
          <v-sheet
            class="px-7 pt-7 pb-4 mx-auto text-center d-inline-block"
            color="blue-grey darken-3"
            dark
          >
            <div class="grey--text text--lighten-1 text-body-2 mb-4">
              {{ $t('Tem certeza que deseja excluir o item selecionado?') }}
            </div>

            <v-btn
              :disabled="loading"
              class="ma-1"
              color="grey"
              text
              depressed
              @click="showDeleteConfirm = false"
            >
              Cancelar
            </v-btn>

            <v-btn
              :loading="loading"
              class="ma-1"
              color="error"
              text
              depressed
              @click="confirmDelete()"
            >
              {{ $t('Excluir') }}
            </v-btn>
          </v-sheet>
        </div>
      </v-dialog>

      <div class="pagination mx-2">
        <div class="item-pagination block-one"></div>

        <div class="item-pagination block-two">
          <v-pagination
            class="pagination-item"
            v-model="page"
            :length="totalPages"
            :total-visible="9"
          ></v-pagination>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import SelectInst from './components/selectInst.vue'
import SelectCharts from './components/selectCharts.vue'
import ConfigureCharts from './components/configureCharts.vue'
import Result from './components/result.vue'
import ApiService from '../../../../../services/ApiService'
import moment from 'moment'

export default {
  name: 'GenerateReportPage',

  components: {
    SelectInst,
    SelectCharts,
    ConfigureCharts,
    Result,
  },

  created() {
    this.user = JSON.parse(localStorage.getItem('user'))
    this.institution = JSON.parse(localStorage.getItem('institution'))
    this.getGenerateds()
    this.checkDraft()
  },

  mounted() {
    this._beforeUnloadHandler = () => {
      if (this.step > 0 && !this.isEditing) {
        localStorage.setItem(
          this.getDraftKey(),
          JSON.stringify({
            step: this.step,
            generalInfo: this.generalInfo,
            selection: this.selection,
            chartInfos: this.chartInfos,
            savedAt: new Date().toISOString(),
          })
        )
      }
    }
    window.addEventListener('beforeunload', this._beforeUnloadHandler)
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this._beforeUnloadHandler)
  },

  data() {
    return {
      user: {},
      editKey: false,
      newFormKey: false,
      apiService: new ApiService(),
      step: 0,
      selection: [],
      generalInfo: {},
      reportId: '',
      chartInfos: [],
      reports: [],
      page: 1,
      isEditing: false,
      showDeleteConfirm: false,
      itemToDelete: null,
      loading: false,
      loadingReports: true,
      headers: [
        {
          text: '',
          align: 'center',
          value: '',
          sortable: false,
        },
        {
          text: 'Link de acesso',
          align: 'center',
          value: 'link',
          sortable: false,
        },
        {
          text: 'Criado em',
          align: 'center',
          value: 'createdAt',
          sortable: false,
        },
        {
          text: '',
          align: 'center',
          value: 'actions',
          sortable: false,
        },
      ],
      totalPages: 0,
      key: false,
      draftSnackbar: false,
    }
  },

  methods: {
    openDeleteConfirm(item) {
      this.itemToDelete = item
      this.showDeleteConfirm = true
    },

    confirmDelete() {
      if (!this.itemToDelete) return

      this.loading = true
      this.apiService
        .delete(`generatedReport/${this.itemToDelete.id}`)
        .then((response) => {
          this.getGenerateds()
          this.$toast.success(`Relatório removido com sucesso!`)
          this.showDeleteConfirm = false
          this.itemToDelete = null
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },

    reset() {
      this.clearDraft()
      this.isEditing = false
      this.reportId = null
      this.generalInfo = {}
      this.selection = []
      this.chartInfos = []
      this.step = 0
    },

    getDraftKey() {
      return `report_draft_${this.institution?.id}`
    },

    checkDraft() {
      const raw = localStorage.getItem(this.getDraftKey())
      if (raw) {
        this.draftSnackbar = true
      }
    },

    loadDraft() {
      const raw = localStorage.getItem(this.getDraftKey())
      if (!raw) return
      const draft = JSON.parse(raw)
      this.generalInfo = draft.generalInfo || {}
      this.selection = draft.selection || []
      this.chartInfos = draft.chartInfos || []
      this.step = draft.step || 1
      this.draftSnackbar = false
    },

    clearDraft() {
      localStorage.removeItem(this.getDraftKey())
      this.draftSnackbar = false
    },

    saveDraft() {
      if (this.step === 0 || this.isEditing) return
      localStorage.setItem(
        this.getDraftKey(),
        JSON.stringify({
          step: this.step,
          generalInfo: this.generalInfo,
          selection: this.selection,
          chartInfos: this.chartInfos,
          savedAt: new Date().toISOString(),
        })
      )
      this.$toast.success(this.$t('Rascunho salvo com sucesso!'))
    },

    onSaveDraft(chartInfos) {
      this.chartInfos = chartInfos
      this.saveDraft()
    },

    async refreshData() {
      await this.apiService
        .post('generatedReport/refresh/' + this.reportId)
        .then(async (response) => {
          await this.getReport(this.reportId, false)
          this.$toast.success('Indicadores atualizados! 📊')
        })
        .catch(() => {
          this.$toast.error('Erro ao atualizar os dados do relatório!')
        })
    },

    async getReport(id, scrollToTop = true) {
      this.loading = true
      await this.apiService
        .get(`generatedReport/${id}`)
        .then((response) => {
          this.isEditing = true
          this.reportId = id
          this.report = response.content

          const json = JSON.parse(this.report.content)

          this.generalInfo = {
            id: id,
            unidadeIds: json.chartInfos[0] ? json.chartInfos[0].unidadeIds : [],
            reviser: json.reviser,
            institutionName: this.report.institution.name,
            toCareUser: json.toCareUser,
            lastEvents: json.lastEvents,
            nextEvents: json.nextEvents,
            link: this.report.link,
          }

          const chartInfoMap = new Map()
          for (const info of json.chartInfos) {
            chartInfoMap.set(info.chart, info)
          }
          const deduplicatedInfos = Array.from(chartInfoMap.values())

          this.selection = deduplicatedInfos.map((x) => x.chart)
          this.chartInfos = deduplicatedInfos

          this.loading = false
          if (scrollToTop) this.$vuetify.goTo(0)
        })
        .catch(() => {
          this.loading = false
        })
    },

    copy(link) {
      navigator.clipboard.writeText(
        process.env.VUE_APP_BASE_URL_FRONT + 'generatedReport/' + link
      )
      this.$toast.success('URL de acesso copiada para a área de transferência!')
    },

    formatDate(date) {
      return date ? moment(date).format('DD/MM/yyyy') : $t('Sem registros')
    },

    getGenerateds() {
      this.loadingReports = true
      this.apiService
        .get(
          `generatedReport/list-paginate/${this.institution.id}/${this.page}`,
          null,
          false,
          false
        )
        .then((response) => {
          this.reports = response.content.reports
          this.totalPages = response.content.pager.totalPages
        })
        .finally(() => {
          this.loadingReports = false
        })
    },

    setChartInfos(infos) {
      this.chartInfos = infos
      this.key = !this.key
      this.step++
    },

    setGeneralInfo(model) {
      if (
        JSON.stringify(this.generalInfo.unidadeIds) !=
        JSON.stringify(model.unidadeIds)
      ) {
        this.chartInfos = this.chartInfos.map((x) => {
          x.data = null
          x.unidadeIds = model.unidadeIds
          return x
        })
      }

      this.generalInfo = model

      this.step++
      this.key = !this.key
    },

    setSelection(value) {
      this.selection = value
      this.step++
    },
  },

  watch: {
    page() {
      this.getGenerateds()
    },
  },
}
</script>

<style scoped>
.reports-step-window {
  overflow: visible !important;
}
</style>
